import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import setUpAxios from "../setUpAxios";
import LoadingScreen from "./reusable/loadingScreen";
import { FaSearch, FaPlus, FaChevronLeft } from "react-icons/fa";
import { useLoginContext } from "../Contexts/LoginContext";
import { useTranslation } from "react-i18next";

function CustomerTable() {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { admin } = useLoginContext();
  setUpAxios();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/users`);
        if (Array.isArray(data)) {
          setCustomers(data);
        } else {
          setError("Unexpected response format.");
        }
      } catch (err) {
        setError(t("customers.error"));
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, [t]);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return customers;
    const lowerQuery = searchQuery.toLowerCase();
    return customers.filter(
      (c) =>
        c.name?.toLowerCase().includes(lowerQuery) ||
        c.mobile_number?.includes(lowerQuery) ||
        c.username?.toLowerCase().includes(lowerQuery)
    );
  }, [customers, searchQuery]);

  const getInitials = (name) => {
    return name ? name.charAt(0).toUpperCase() : "?";
  };

  if (loading) return <LoadingScreen message={t("customers.loading")} />;
  if (error) return <div className="p-10 text-center text-debit font-medium">{error}</div>;
  if (!admin) return <div className="p-10 text-center text-[#6B7280]">{t("customers.access_denied")}</div>;

  return (
    <div className="min-h-screen max-w-lg mx-auto w-full bg-paper pb-10 font-sans text-ink">
      {/* --- Ledger Styled Header --- */}
      <header className="px-5 pt-5 pb-2 sticky top-0 bg-paper z-40">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 -ml-2 rounded-full hover:bg-black/5 active:scale-90 transition-all duration-150 flex-shrink-0"
              aria-label="Go Back"
            >
              <FaChevronLeft className="text-ink text-lg" />
            </button>
            <h1 className="font-slab font-bold text-[20px] text-ink tracking-wide mt-0.5 truncate">
              {t("customers.title", "Customers")}
            </h1>
          </div>
          <button
            onClick={() => navigate("/addCustomer")}
            className="w-9 h-9 bg-primary text-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-all duration-150"
            aria-label="Add Customer"
          >
            <FaPlus size={14} />
          </button>
        </div>

        {/* Signature Ledger Double Rule */}
        <div className="w-full">
          <div className="h-[2px] rounded bg-primary" />
          <div className="h-[1px] mt-[3px] bg-line" />
        </div>
      </header>

      <main className="px-5 mt-5">
        {/* --- Search Bar --- */}
        <div className="relative mb-5">
          <FaSearch
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]"
            size={14}
          />
          <input
            type="text"
            placeholder={t(
              "customers.search_placeholder",
              "Search by name or phone",
            )}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-ledgerline rounded-xl focus:outline-none focus:border-ink focus:ring-1 focus:ring-ink transition-all text-[13.5px] font-medium text-ink placeholder-[#6B7280] shadow-sm"
          />
        </div>

        {/* --- Results Count --- */}
        <p className="text-[11px] font-bold uppercase tracking-widest text-ink mb-3 ml-0.5">
          {filtered.length} {t("customers.found", "customers found")}
        </p>

        {/* --- Customer List --- */}
        {filtered.length > 0 ? (
          <div className="space-y-3">
            {filtered.map((c) => {
              const balance = Number(c.total || 0);

              return (
                <div
                key={c.id}
                onClick={() => navigate(`/user/${c.id}`, { state: c })}
                className="bg-white border border-ledgerline rounded-2xl p-4 flex items-center gap-4 cursor-pointer shadow-sm active:scale-[0.98] transition-transform"
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {c.profile_img ? (
                    <img
                      src={c.profile_img}
                      alt={c.name}
                      className="w-12 h-12 rounded-full object-cover border-[2px] border-paper"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-paper border border-dashed border-ledgerline text-ink flex items-center justify-center font-slab font-bold text-lg">
                      {getInitials(c.name)}
                    </div>
                  )}
                </div>

                {/* Info Section */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-ink font-bold text-[15px] truncate capitalize leading-tight">
                    {c.name}
                  </h3>
                  <p className="text-[#6B7280] text-[12px] font-medium mt-0.5">
                    {c.mobile_number || t("customers.no_number")}
                  </p>
                </div>

                {/* Balance Section */}
                <div className="text-right flex-shrink-0">
                  <span className="block text-[9.5px] font-bold text-ink uppercase tracking-widest mb-0.5">
                    {t("customers.balance_label")}
                  </span>
                  <span className={`block font-bold text-[15px] ${balance >= 0 ? 'text-credit' : 'text-debit'}`}>
                    {balance >= 0 ? '+' : '-'} ₹
                    {Math.abs(balance).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          /* --- Empty State --- */
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="bg-paper border border-dashed border-ledgerline p-5 rounded-full mb-4">
              <FaSearch className="text-2xl text-ledgerline" />
            </div>
            <h3 className="text-ink font-bold text-lg">
              {t("customers.empty_title")}
            </h3>
            <p className="text-[#6B7280] text-[13px] mt-1.5 max-w-[220px] leading-relaxed">
              {t("customers.empty_msg")}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

export default CustomerTable;
